import * as _ from 'underscore'

import { Enums } from "../../../models/common"
import { OpDashboard, OpDashboardData } from '../../../models/custom'
import { OperatorLayoutSetting } from "../../../models/local"

import { UserSessionService } from '../../../services/user-session'
import { SystemService } from '../../../services/system'

import { IMRIFrameWrapperMessage } from '../../common/iframe-wrapper'
import { LogHelper } from '../../../helpers'


// Public Types

export const SERVICE_NAME: string = 'IMRDXDashboardService'

export type DXDashboardFilter = {
    name: string,
    state: string
}

export type DXDashboardSettings = {
    currentFilter: string
    filters: DXDashboardFilter[]
    isParamEditOpen: boolean
    isParamEditPinned: boolean
}

// Public Types End


export class DXDashboardService {

    //#region Consts

    public static get LOG_PREFIX(): string { return '[SIMAX.Module.Dashboard.DXDashboardSimple]' }

    private static get CONTEXT_ACTION_HEADER(): string { return 'CONTEXT_ACTION_HEADER' }
    private static get REQUEST_DXDASHBOARD_CONFIG_HEADER(): string { return 'REQUEST_DXDASHBOARD_CONFIG_HEADER' }
    private static get RESPONSE_DXDASHBOARD_CONFIG_HEADER(): string { return 'RESPONSE_DXDASHBOARD_CONFIG_HEADER' }

    //#endregion


    //#region Ctor

    constructor(
        private systemService: SystemService,
        private userSession: UserSessionService
    ) { }

    //#endregion


    // Public

    public getCurrentFilters(idDashboard: number): DXDashboardFilter[] {
        return this.loadDashboardSettings(idDashboard).filters
    }

    public getDashboardIndex(dashboard: OpDashboard): number {
        const data: OpDashboardData = _.find(dashboard.dataList.toArray(), (d) => d.idDataType === Enums.DataType.DASHBOARD_DX_PATH)

        return data != null ? data.indexNBR : 0
    }

    public setCurrentFilters(idDashboard: number, filters: DXDashboardFilter[]) {
        const settings: DXDashboardSettings = this.loadDashboardSettings(idDashboard)

        settings.filters = filters

        this.saveDashboardSettings(idDashboard, settings)
    }

    public getCurrentFilterName(idDashboard: number): string {
        return this.loadDashboardSettings(idDashboard).currentFilter
    }

    public setCurrentFilterName(idDashboard: number, filterName: string) {
        const settings: DXDashboardSettings = this.loadDashboardSettings(idDashboard)

        settings.currentFilter = filterName

        this.saveDashboardSettings(idDashboard, settings)
    }

    public getIsOpen(idDashboard: number): boolean {
        return this.loadDashboardSettings(idDashboard).isParamEditOpen
    }

    public setIsOpen(idDashboard: number, isOpen: boolean) {
        const settings: DXDashboardSettings = this.loadDashboardSettings(idDashboard)

        settings.isParamEditOpen = isOpen

        this.saveDashboardSettings(idDashboard, settings)
    }

    public getIsPinned(idDashboard: number): boolean {
        return this.loadDashboardSettings(idDashboard).isParamEditPinned
    }

    public setIsPinned(idDashboard: number, isPinned: boolean): void {
        const settings: DXDashboardSettings = this.loadDashboardSettings(idDashboard)

        settings.isParamEditPinned = isPinned

        this.saveDashboardSettings(idDashboard, settings)
    }

    public saveFilter(idDashboard: number, filterToSave: DXDashboardFilter): void {
        if (filterToSave != null) {
            const filterSettings: DXDashboardSettings = this.loadDashboardSettings(idDashboard)
            const existingFilter: DXDashboardFilter = _.find(filterSettings.filters, ((filter: DXDashboardFilter) => filter.name === filterToSave.name))

            if (existingFilter != null) {
                existingFilter.state = filterToSave.state
            }
            else {
                filterSettings.filters.push(filterToSave)
            }

            this.saveDashboardSettings(idDashboard, filterSettings)
        }
    }

    // Public End


    //#region IFrame Message

    public handleIFrameMessage(message: IMRIFrameWrapperMessage<any>, dashboard: OpDashboard) {
        const dashboardIndex: number = this.getDashboardIndex(dashboard)

        if (message.headers[DXDashboardService.REQUEST_DXDASHBOARD_CONFIG_HEADER]) {
            LogHelper.log(DXDashboardService.LOG_PREFIX, 'Received config request message.')

            const config = {
                AUTH: {
                    USERNAME: this.userSession.getLoggedOperator().toString(),
                    SESSION_TOKEN: dashboard.dataList.getValue(Enums.DataType.DASHBOARD_DX_TOKEN, dashboardIndex),
                },

                DASHBOARD_STANDARD: {
                    GRID: {
                        AUTO_FILTER_ROW_VISIBLE: dashboard.dataList.getValue(Enums.DataType.DASHBOARD_DX_FILTER_ROW_VISIBLE, dashboardIndex),
                    }
                },

                LANGUAGE: {
                    CURRENT: this.userSession.getDataProvider().System.getCurrentLanguage(),
                },
            }

            message.respond({
                headers: {
                    [DXDashboardService.RESPONSE_DXDASHBOARD_CONFIG_HEADER]: true,
                },

                message: config
            })

            LogHelper.log(DXDashboardService.LOG_PREFIX, `Responding with config: %O.`, config)
        }

        if (message.headers[DXDashboardService.CONTEXT_ACTION_HEADER]) {
            if (message.data != null && _.isArray(message.data.selection) && _.isFinite(message.data.parameter)) {
                let value: any = _.first(message.data.selection)

                if (_.isFinite(value)) {
                    value = [Number(value)]
                }

                if (_.isString(value)) {
                    value = value.toString().split(',').map((id: string) => Number(id))
                }

                LogHelper.log(DXDashboardService.LOG_PREFIX, `Received context action data: %O.`, message.data)

                this.systemService.openDetailsOrModule({
                    idReferenceType: message.data.parameter,
                    referenceObjectIds: value
                })
            }
        }
    }

    //#endregion


    // Private Methods 

    private loadDashboardSettings(idDashboard: number): DXDashboardSettings {
        const settings = this.userSession.getOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.DXDashboard));

        if (settings != null) {
            const dashboardSettings: DXDashboardSettings = settings[idDashboard]

            if (dashboardSettings != null) {
                return dashboardSettings
            }
        }

        return {
            currentFilter: null,
            filters: [],
            isParamEditOpen: false,
            isParamEditPinned: true,
        }
    }

    private saveDashboardSettings(idDashboard: number, settings: DXDashboardSettings): void {
        let allSettings: any = this.userSession.getOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.DXDashboard));

        if (allSettings == null) {
            allSettings = {}
        }

        allSettings[idDashboard] = settings

        this.userSession.setOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.DXDashboard), allSettings)
    }

    // Private Methods End
}